import { Link } from "react-router-dom";
import { formatUSD, formatPct } from "@/lib/format";

const medal = (rank) => (rank === 1 ? "01" : rank === 2 ? "02" : "03");

export default function Leaderboard({ traders }) {
  return (
    <section data-testid="leaderboard" className="border border-arena-line bg-white">
      <div className="px-6 lg:px-8 py-4 border-b border-arena-line flex items-baseline justify-between">
        <h2 className="font-display font-black text-2xl tracking-tighter">LEADERBOARD</h2>
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
          Total portfolio value · risk-adjusted metrics
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 arena-grid">
        {traders.map((t) => {
          const positive = t.pnl >= 0;
          const m = t.metrics || {};
          return (
            <Link
              key={t.id}
              to={`/traders/${t.id}`}
              data-testid={`leaderboard-card-${t.id}`}
              className="bg-white p-6 lg:p-8 flex flex-col gap-4 relative overflow-hidden group no-underline text-arena-ink hover:bg-arena-alt/30 transition-colors"
            >
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: t.color }} />
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: t.color }}>
                    Rank {medal(t.rank)}
                  </div>
                  <div className="font-display font-black text-3xl tracking-tighter mt-1">
                    {t.name}
                  </div>
                  <div className="text-xs text-arena-soft mt-1">{t.tagline}</div>
                </div>
                <div
                  className="w-12 h-12 flex items-center justify-center font-display font-black text-lg"
                  style={{ background: t.color, color: "#fff" }}
                >
                  {medal(t.rank)}
                </div>
              </div>

              <div className="mt-2">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
                  Portfolio
                </div>
                <div className="font-mono font-bold text-4xl tabular-nums mt-1" data-testid={`portfolio-value-${t.id}`}>
                  {formatUSD(t.total_value)}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-px bg-arena-line border-t border-arena-line">
                <div className="bg-white pt-3 pr-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft">P/L</div>
                  <div className={`font-mono font-bold text-base tabular-nums ${positive ? "text-profit" : "text-loss"}`}>
                    {formatUSD(t.pnl, { sign: true })}
                  </div>
                </div>
                <div className="bg-white pt-3 pl-3 pr-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft">Return</div>
                  <div className={`font-mono font-bold text-base tabular-nums ${positive ? "text-profit" : "text-loss"}`}>
                    {formatPct(t.pnl_pct)}
                  </div>
                </div>
                <div className="bg-white pt-3 pl-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft">Cash</div>
                  <div className="font-mono font-bold text-base tabular-nums">
                    {formatUSD(t.cash, { compact: true })}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-px bg-arena-line border-t border-arena-line">
                <div className="bg-white pt-3 pr-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft">Sharpe</div>
                  <div className="font-mono font-bold text-sm tabular-nums">
                    {(m.sharpe ?? 0).toFixed(2)}
                  </div>
                </div>
                <div className="bg-white pt-3 pl-3 pr-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft">Max DD</div>
                  <div className="font-mono font-bold text-sm tabular-nums text-loss">
                    {(m.max_drawdown_pct ?? 0).toFixed(2)}%
                  </div>
                </div>
                <div className="bg-white pt-3 pl-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft">Vol</div>
                  <div className="font-mono font-bold text-sm tabular-nums">
                    {(m.volatility_pct ?? 0).toFixed(2)}%
                  </div>
                </div>
              </div>

              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft mt-2 group-hover:text-arena-ink transition-colors">
                View detail →
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
